import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $cardLimitsStore, $cardStore, $profileFeatureStore } from '@lib/store';
import { useCardFlow, useCurrencySymbol } from '@shared/hooks';
import { cn, PaletteUtils } from '@shared/utils';
import { Frequency } from '@shared/api/rest/cards';
import { AccountInfo, AccountInfoRow, Users, CardSidebar, TransactionsTable } from '@widgets';
import { NoData, PageContainer } from '@shared/widgets';

export const CardPage = () => {
  const { id } = useParams<{ id: string }>();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const cardStore = useUnit($cardStore.store);
  const cardLimitsStore = useUnit($cardLimitsStore.store);

  const { t } = useTranslation('card');

  useEffect(() => {
    if (!id) return;
    const idNumber = parseInt(id);
    $cardStore.events.getCard(idNumber);

    const currentCardPermission = profileFeatureStore.data?.cardFlows.find((x) => x.cardId === idNumber);
    if (currentCardPermission?.viewCardLimit) $cardLimitsStore.events.getCardLimits(idNumber);

    return () => {
      $cardStore.events.clear();
      $cardLimitsStore.events.clear();
    };
  }, [id]);

  const cardFlow = useCardFlow(profileFeatureStore.data?.cardFlows, id ? parseInt(id) : undefined);
  const symbol = useCurrencySymbol(cardStore.data?.currency);
  const balance = useMemo<number | undefined>(() => {
    if (!cardLimitsStore.data) return undefined;
    let amount = 0;
    const frequency = cardLimitsStore.data.frequency;
    if (frequency === Frequency.DAILY) amount = cardLimitsStore.data.dayLimits?.otcAmount || 0;
    else if (frequency === Frequency.CYCLE) amount = cardLimitsStore.data.cycleLimits?.otcAmount || 0;
    else if (frequency === Frequency.YEAR) amount = cardLimitsStore.data.yearLimits?.otcAmount || 0;
    return Math.min(cardLimitsStore.data.currentOTCAmount - amount, cardLimitsStore.data.currentOTBAmount);
  }, [cardLimitsStore.data]);
  return (
    <PageContainer loading={cardStore.loading} title={t('title')} back backText={t('backText')} return>
      {!cardStore.data && <NoData />}
      {cardStore.data && (
        <div className={cn('flex', PaletteUtils.isVcfcPalette && 'max-md:block')}>
          <div
            className={cn('flex flex-auto flex-col', PaletteUtils.isVcfcPalette && 'rounded-lg border border-gray-black bg-white dark:bg-soft-black')}
          >
            <div className={cn(PaletteUtils.isInsigniaPalette && 'mb-12')}>
              <AccountInfo
                title={cardStore.data.cardLabel}
                type1={cardStore.data.productDisplayName}
                user={cardStore.data.cardHolder}
                amount={balance}
                status={cardStore.data.status}
                type2={t('accountInfo.fields.type2')}
                icon={symbol}
                card={cardStore.data}
              >
                <AccountInfoRow divider />
                <AccountInfoRow text={t('accountInfo.fields.cardNumber')} number={cardStore.data.cardNumber} />
                <AccountInfoRow text={t('accountInfo.fields.accountNumber')} number={cardStore.data.accountNumber} />
              </AccountInfo>
            </div>
            {profileFeatureStore.data?.userManagement?.managerAccountsOrCardsPermissions && id && <Users cardId={id} />}
            {cardFlow?.viewTransactions && <TransactionsTable cardId={cardStore.data.cardId} />}
          </div>
          <div className={cn('ml-8 w-80 flex-none', PaletteUtils.isVcfcPalette && 'max-md:ml-0 max-md:w-full')}>
            <CardSidebar card={cardStore.data} />
          </div>
        </div>
      )}
    </PageContainer>
  );
};
